import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { teamMembers } from "./team.data";
import { useFadeInScroll } from "../../components/Animation/fadeinScroll";

const AboutTeamSection = () => {
  const sliderRef = useRef<HTMLDivElement | null>(null);
  const snapRestoreRAF = useRef<number | null>(null);

  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const title = useFadeInScroll<HTMLDivElement>();

  /* -------------------- Scroll State -------------------- */
  const updateScrollState = () => {
    if (!sliderRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;

    setCanScrollLeft(scrollLeft > 10);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 10);
  };

  /* -------------------- Smooth Scroll (NO BLINK FIX) -------------------- */
const scrollCarousel = (direction: "left" | "right") => {
  if (!sliderRef.current) return;

  const container = sliderRef.current;

  // Get first card
  const card = container.querySelector<HTMLElement>("[data-carousel-card]");
  if (!card) return;

  // Card width + gap
  const cardWidth = card.offsetWidth;
  const gap = parseFloat(getComputedStyle(container).columnGap || "24");
  const scrollAmount = cardWidth + gap;

  const maxScroll = container.scrollWidth - container.clientWidth;

  const target =
    direction === "left"
      ? Math.max(0, container.scrollLeft - scrollAmount)
      : Math.min(maxScroll, container.scrollLeft + scrollAmount);

  // Disable snap during manual scroll
  container.style.scrollSnapType = "none";

  container.scrollTo({
    left: target,
    behavior: "smooth",
  });

  // Restore snap after scroll settles
  let lastLeft = container.scrollLeft;

  const restoreSnap = () => {
    const currentLeft = container.scrollLeft;
    if (Math.abs(currentLeft - lastLeft) < 0.5) {
      container.style.scrollSnapType = "x mandatory";
      return;
    }
    lastLeft = currentLeft;
    requestAnimationFrame(restoreSnap);
  };

  requestAnimationFrame(restoreSnap);
};


  /* -------------------- Effects -------------------- */
  useEffect(() => {
    updateScrollState();
    const el = sliderRef.current;
    if (!el) return;

    el.addEventListener("scroll", updateScrollState);
    return () => el.removeEventListener("scroll", updateScrollState);
  }, []);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") scrollCarousel("right");
      if (e.key === "ArrowLeft") scrollCarousel("left");
    };

    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  useEffect(() => {
    const blockKeys = (e: KeyboardEvent) => {
      if (
        e.key === "PrintScreen" ||
        (e.ctrlKey && e.key === "p") ||
        (e.metaKey && e.shiftKey && ["3", "4", "5"].includes(e.key))
      ) {
        e.preventDefault();
        alert("Screenshots are disabled for protected content.");
      }
    };

    window.addEventListener("keydown", blockKeys);
    return () => window.removeEventListener("keydown", blockKeys);
  }, []);

  /* -------------------- JSX -------------------- */
  return (
    <section className="bg-bg-secondary">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="max-w-3xl mb-12">
          <div
            ref={title.ref}
            className={`transition-all duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)]
              ${title.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}
            `}
          >
            <h2 className="text-subtitle text-primary font-primary mb-4">
             Meet Our Team
            </h2>
          </div>

          <p className="text-body text-text-secondary">
            Our multidisciplinary team combines clinical expertise, design
            precision, and manufacturing excellence to build patient-first
            orthopedic solutions.
          </p>
        </div>

        {/* Carousel */}
        <div className="grid grid-cols-1 lg:grid-cols-[auto_1fr_auto] items-center gap-4">
          {/* Left Arrow */}
          <div className="hidden lg:flex justify-center">
            {canScrollLeft && (
              <button
                onClick={() => scrollCarousel("left")}
                className="w-11 h-11 rounded-full flex items-center justify-center
                  bg-bg-primary border border-border-primary
                  shadow-md hover:scale-110 transition"
              >
                <ChevronLeft className="text-primary" />
              </button>
            )}
          </div>

          {/* Scroll Container */}
          <div
            ref={sliderRef}
            className="flex gap-6 overflow-x-auto snap-x snap-mandatory pb-6
              [-ms-overflow-style:none] [scrollbar-width:none]
              [&::-webkit-scrollbar]:hidden"
          >
            {teamMembers.map((member, index) => (
              <div
              data-carousel-card
                className="
                  min-w-[85%]
                  sm:min-w-[60%]
                  md:min-w-[45%]
                  lg:min-w-[30%]
                  snap-center
                  bg-bg-primary
                  border border-border-primary
                  rounded-2xl
                  overflow-hidden
                  transition-all duration-500
                "
              >
                <div className="p-5 pb-0">
                  <div className="relative h-60 sm:h-64 md:h-68  rounded-xl overflow-hidden bg-gray-100">
                    <img
                      src={member.image}
                      alt={member.name}
                      draggable={false}
                       
                    />
                    <div
                      className="absolute inset-0 z-10"
                      onContextMenu={(e) => e.preventDefault()}
                    />
                  </div>
                </div>

                <div className="p-5 text-center">
                  <h3 className="text-base sm:text-lg font-semibold text-primary">
                    {member.name}
                  </h3>
                  <p className="text-xs sm:text-sm text-text-secondary mt-1">
                    {member.role}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Right Arrow */}
          <div className="hidden lg:flex justify-center">
            {canScrollRight && (
              <button
                onClick={() => scrollCarousel("right")}
                className="w-11 h-11 rounded-full flex items-center justify-center
                  bg-bg-primary border border-border-primary
                  shadow-md hover:scale-110 transition"
              >
                <ChevronRight className="text-primary" />
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutTeamSection;
